import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Star } from "lucide-react";
import { useState, useMemo } from "react";
import { Badge } from "@/components/ui/badge";

interface RatingGoalCalculatorProps {
  currentAverage: number;
  totalReviews: number;
}

const STAR_RATINGS = [5, 4, 3, 2, 1] as const;

interface CalculationResult {
  starRating: number;
  reviewsNeeded: number;
}

export function RatingGoalCalculator({
  currentAverage,
  totalReviews
}: RatingGoalCalculatorProps) {
  const [desiredAvg, setDesiredAvg] = useState<string>("");

  // Calculate reviews needed for a specific star rating
  const calculateReviewsNeeded = (
    currentAvg: number,
    desiredAvg: number,
    totalReviews: number,
    starRating: number
  ): number | null => {
    // Avoid division by zero
    if (Math.abs(desiredAvg - starRating) < 0.001) return null;

    const numerator = (currentAvg - desiredAvg) * totalReviews;
    const denominator = desiredAvg - starRating;
    const result = numerator / denominator;

    // Must be positive and realistic
    if (result < 0 || result > 100000) return null;

    return Math.ceil(result);
  };

  // Get relevant star ratings based on goal direction
  const getRelevantStarRatings = (
    currentAvg: number,
    desiredAvg: number
  ): number[] => {
    if (Math.abs(desiredAvg - currentAvg) < 0.01) {
      return []; // Goal achieved
    }

    // Only consider whole star ratings: 5, 4, 3, 2, 1
    const allRatings = [5, 4, 3, 2, 1];

    if (desiredAvg > currentAvg) {
      // Need to raise: only show ratings ABOVE desired average
      return allRatings.filter(r => r > desiredAvg);
    } else {
      // Need to lower: only show ratings BELOW desired average
      return allRatings.filter(r => r < desiredAvg);
    }
  };

  // Calculate results
  const results = useMemo(() => {
    if (!desiredAvg || totalReviews === 0) return null;

    const desiredNum = parseFloat(desiredAvg);

    // Validation
    if (isNaN(desiredNum) || desiredNum < 1.0 || desiredNum > 5.0) {
      return { error: "Please enter a value between 1.0 and 5.0" };
    }

    // Check if goal already achieved
    if (Math.abs(desiredNum - currentAverage) < 0.01) {
      return { achieved: true };
    }

    const relevantRatings = getRelevantStarRatings(currentAverage, desiredNum);

    if (relevantRatings.length === 0) {
      return { noOptions: true };
    }

    const calculations: CalculationResult[] = [];

    relevantRatings.forEach(rating => {
      const needed = calculateReviewsNeeded(currentAverage, desiredNum, totalReviews, rating);
      if (needed !== null && needed > 0) {
        calculations.push({ starRating: rating, reviewsNeeded: needed });
      }
    });

    if (calculations.length === 0) {
      return { noOptions: true };
    }

    return { calculations, isRaising: desiredNum > currentAverage };
  }, [desiredAvg, currentAverage, totalReviews]);

  // Render star icons
  const renderStars = (count: number) => {
    return (
      <div className="flex">
        {[...Array(count)].map((_, i) => (
          <Star
            key={i}
            size={12}
            className="text-yellow-500 fill-yellow-500"
          />
        ))}
      </div>
    );
  };

  // Get color class based on difficulty
  const getDifficultyColor = (reviewsNeeded: number) => {
    if (reviewsNeeded <= 50) return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300";
    if (reviewsNeeded <= 200) return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300";
    return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300";
  };

  // Show message if no reviews
  if (totalReviews === 0) {
    return (
      <Card className="w-full">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
          <CardTitle className="text-sm font-medium">
            Rating Goal Calculator
          </CardTitle>
        </CardHeader>
        <CardContent className="pb-4">
          <p className="text-sm text-muted-foreground">
            Add some reviews first to use the rating goal calculator.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
        <CardTitle className="text-sm font-medium">
          Rating Goal Calculator
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 pb-4">
        {/* Current Stats */}
        <div className="flex items-center gap-3 text-xs">
          <div className="flex items-center gap-1">
            <span className="text-muted-foreground">Current:</span>
            <span className="font-semibold">{currentAverage.toFixed(1)}</span>
            <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
          </div>
          <div className="text-muted-foreground">
            ({totalReviews} {totalReviews === 1 ? 'review' : 'reviews'})
          </div>
        </div>

        {/* Input */}
        <div className="space-y-1.5">
          <Label htmlFor="desired-avg" className="text-xs">
            Desired Average Rating
          </Label>
          <Input
            id="desired-avg"
            type="number"
            step="0.1"
            min="1.0"
            max="5.0"
            placeholder="e.g., 4.5"
            value={desiredAvg}
            onChange={(e) => setDesiredAvg(e.target.value)}
            className="h-8 text-sm"
          />
          <p className="text-xs text-muted-foreground">
            Enter a value between 1.0 and 5.0
          </p>
        </div>

        {/* Results */}
        {results && (
          <div className="space-y-2">
            {results.error && (
              <div className="p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                <p className="text-xs text-red-800 dark:text-red-300">{results.error}</p>
              </div>
            )}

            {results.achieved && (
              <div className="p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
                <p className="text-xs text-green-800 dark:text-green-300 flex items-center gap-2">
                  <span>✓</span>
                  <span>Goal already achieved!</span>
                </p>
              </div>
            )}

            {results.noOptions && (
              <div className="p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                <p className="text-xs text-yellow-800 dark:text-yellow-300">
                  No valid star ratings can help reach this goal.
                </p>
              </div>
            )}

            {results.calculations && results.calculations.length > 0 && (
              <div className="space-y-1.5">
                <div className="text-xs font-medium">
                  To {results.isRaising ? 'reach' : 'lower to'} {parseFloat(desiredAvg).toFixed(1)} stars:
                </div>
                <p className="text-xs text-muted-foreground">
                  Choose <span className="font-semibold">ONE</span> strategy:
                </p>
                <div className="space-y-1.5">
                  {results.calculations.map(({ starRating, reviewsNeeded }) => (
                    <div
                      key={starRating}
                      className="flex items-center justify-between p-1.5 border rounded-md bg-background"
                    >
                      <div className="flex items-center gap-1.5">
                        {renderStars(starRating)}
                        <span className="text-xs font-medium">{starRating} {starRating === 1 ? 'star' : 'stars'}</span>
                      </div>
                      <Badge variant="outline" className={`text-xs ${getDifficultyColor(reviewsNeeded)}`}>
                        {reviewsNeeded} needed
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
